import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Layout, Menu, Flex, Row, Col, Typography, Spin, Button, Drawer, Grid, Switch } from 'antd';
import { HomeOutlined, ProfileOutlined, LogoutOutlined, LoginOutlined, CarOutlined, WalletOutlined, MenuOutlined, UserOutlined, BgColorsOutlined, BulbOutlined } from '@ant-design/icons';
import { Link, Routes, Route, useLocation } from 'react-router-dom';
import Home from '../../Pages/Home.js'
import Cabinet from '../../Pages/Cabinet.js'
import SignIn from '../../Pages/SignIn.js';
import Logout from '../../Pages/Logout.js';
import { ReactComponent as MasterCardLogo } from '../../img/MasterCard_Logo.svg'
import { ReactComponent as VisaLogo } from '../../img/visa.svg'
import ProtectedRoute from '../../routing/ProtectedRoute.js';
import Register from '../../Pages/Register.js';
import { AnimatePresence } from "framer-motion";
import Verify from '../../Pages/Verify.js';
import About from '../../Pages/About.js';
import Contract from '../../Pages/Contract.js';
import Parking from '../../Pages/Parking.js';
import { useGetUserDetailsQuery } from '../../services/auth/authService.js';
import { ThemeModeContext } from '../../App.js';

const { Header, Content, Footer } = Layout;
const { Text, Title } = Typography;
const { useBreakpoint } = Grid;

const LayoutComponent = () => {
  let location = useLocation();
  const screens = useBreakpoint();
  const { themeMode, setThemeMode } = useContext(ThemeModeContext);
  const { userToken } = useSelector((state) => state.auth);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { data: userInfo = {}, isFetching: isUserInfoFetching } = useGetUserDetailsQuery(undefined, {
    skip: userToken === null,
  });
  const isXsNavigation = !screens.lg;
  const menuTheme = themeMode === 'dark' ? 'dark' : 'light';
  const authorizedUser = [userInfo.first_name, userInfo.last_name].filter(Boolean).join(' ') || 'Користувач';
  const openProfileTab = () => {
    localStorage.setItem('tab', 'profile');
  };
  const parkingBalance = userInfo.parking_balance ?? 0;
  const onThemeChange = (checked) => {
    setThemeMode(checked ? 'dark' : 'light');
  };
  useEffect(() => {
    setMobileMenuOpen(false);
  }, [location.pathname]);
  const menuItems = useMemo(() => {
    let items = [];
    items.push({
      label: <Link to="/">Головна</Link>,
      key: '/',
      icon: <HomeOutlined />
    });
    if (userToken !== null) {
      items.push({
        label: <Link to="/profile">Кабінет</Link>,
        key: '/profile',
        icon: <ProfileOutlined />
      });
      items.push({
        label: <Link to="/parking">Паркінг</Link>,
        key: '/parking',
        icon: <CarOutlined />
      });
      items.push({
        label: <Link to="/logout">Вихід</Link>,
        key: '/logout',
        icon: <LogoutOutlined />
      });
    } else {
      items.push({
        label: <Link to="/sign-in">Вхід</Link>,
        key: '/sign-in',
        icon: <LoginOutlined />
      });
    }
    return items;
  }, [userToken]);
  return (
    <Layout>
      <Header style={{
        backgroundColor: 'transparent',
        position: 'sticky',
        top: 0,
        zIndex: 20,
        width: '100%',
        height: 'auto',
        lineHeight: 'normal',
        padding: '12px 16px',
      }} className='site-header'>
        <div className='site-header-shell'>
          <Link to='/' className='site-brand'>
            <span className='site-brand-mark'>G1</span>
            <span className='site-brand-copy'>
              <span className='site-brand-title'>Getman 1</span>
              <span className='site-brand-subtitle'>Особистий кабінет</span>
            </span>
          </Link>
          {userToken !== null && !isXsNavigation && (
            <div className='site-user-actions'>
              <Link to='/profile' className='site-user-chip'>
                <span className='site-user-icon'><UserOutlined /></span>
                <span className='site-user-copy'>
                  <span className='site-user-label'>Авторизовано</span>
                  <span className='site-user-value'>
                    {isUserInfoFetching ? <Spin size='small' /> : authorizedUser}
                  </span>
                  {userInfo.phone && !isUserInfoFetching && (
                    <span className='site-user-phone'>{userInfo.phone}</span>
                  )}
                </span>
              </Link>
              <Link to='/profile' className='site-balance-chip' onClick={openProfileTab}>
                <span className='site-balance-icon'><WalletOutlined /></span>
                <span className='site-balance-copy'>
                  <span className='site-balance-label'>Баланс</span>
                  <span className={`site-balance-value ${Number(userInfo.balance) < 0 ? 'is-negative' : 'is-positive'}`}>
                    {isUserInfoFetching ? <Spin size='small' /> : `${userInfo.balance ?? 0} грн`}
                  </span>
                </span>
              </Link>
              <Link to='/parking' className='site-balance-chip site-balance-chip--parking'>
                <span className='site-balance-icon'><CarOutlined /></span>
                <span className='site-balance-copy'>
                  <span className='site-balance-label'>Паркінг</span>
                  <span className={`site-balance-value ${Number(parkingBalance) < 0 ? 'is-negative' : 'is-positive'}`}>
                    {isUserInfoFetching ? <Spin size='small' /> : `${parkingBalance} грн`}
                  </span>
                </span>
              </Link>
              <div className='site-theme-switch' title='Перемкнути тему'>
                <Switch
                  checked={themeMode === 'dark'}
                  onChange={onThemeChange}
                  checkedChildren={<BulbOutlined />}
                  unCheckedChildren={<BgColorsOutlined />}
                />
              </div>
            </div>
          )}
          {isXsNavigation ? (
            <Button
              className='site-menu-trigger'
              type='text'
              icon={<MenuOutlined />}
              aria-label='Відкрити меню'
              onClick={() => setMobileMenuOpen(true)}
            />
          ) : (
            <div className='site-menu-wrap'>
              <Menu
                className='site-menu'
                theme={menuTheme}
                mode="horizontal"
                defaultSelectedKeys={['/']}
                selectedKeys={[location.pathname]}
                items={menuItems}
              />
            </div>
          )}
        </div>
      </Header>
      <Drawer
        title='Меню'
        placement='top'
        height='auto'
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        className='site-mobile-drawer'
      >
        {userToken !== null && (
          <div className='site-drawer-account'>
            <Link to='/profile' className='site-balance-chip site-balance-chip--drawer' onClick={openProfileTab}>
              <span className='site-balance-icon'><UserOutlined /></span>
              <span className='site-balance-copy'>
                <span className='site-balance-label'>{isUserInfoFetching ? 'Користувач' : authorizedUser}</span>
                <span className='site-user-phone'>{userInfo.phone}</span>
                <span className={`site-balance-value ${Number(userInfo.balance) < 0 ? 'is-negative' : 'is-positive'}`}>
                  {isUserInfoFetching ? <Spin size='small' /> : `Баланс: ${userInfo.balance ?? 0} грн`}
                </span>
              </span>
            </Link>
            <Link to='/parking' className='site-balance-chip site-balance-chip--drawer site-balance-chip--parking'>
              <span className='site-balance-icon'><CarOutlined /></span>
              <span className='site-balance-copy'>
                <span className='site-balance-label'>Паркінг-баланс</span>
                <span className={`site-balance-value ${Number(parkingBalance) < 0 ? 'is-negative' : 'is-positive'}`}>
                  {isUserInfoFetching ? <Spin size='small' /> : `${parkingBalance} грн`}
                </span>
              </span>
            </Link>
          </div>
        )}
        <div className='site-drawer-theme-row'>
          <span className='site-drawer-theme-label'>Тема інтерфейсу</span>
          <Switch
            checked={themeMode === 'dark'}
            onChange={onThemeChange}
            checkedChildren={<BulbOutlined />}
            unCheckedChildren={<BgColorsOutlined />}
          />
        </div>
        <Menu
          className='site-mobile-menu'
          theme={menuTheme}
          selectable
          selectedKeys={[location.pathname]}
          items={menuItems}
        />
      </Drawer>
      <Content className='Main-Content site-content'>
        <AnimatePresence mode='wait' initial={false}>
          <Routes location={location} key={location.pathname}>
            <Route path="/" element={<Home />} />
            <Route element={<ProtectedRoute />}>
              <Route path="/profile" element={<Cabinet />} />
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route path="/parking" element={<Parking />} />
            </Route>
            <Route element={<ProtectedRoute />}>
              <Route path="/verify" element={<Verify />} />
            </Route>
            <Route path="/sign-in" element={<SignIn />} />
            <Route path="/register" element={<Register />} />
            <Route path="/logout" element={<Logout />} />
            <Route path='/about' element={<About/>}/>
            <Route path='/contract' element={<Contract/>} />
            <Route path='/offer_agreement' element={<Contract/>} />
          </Routes>
        </AnimatePresence>
      </Content>
      <Footer className='footer site-footer' style={{ backgroundColor: '#eaf1f4' }}>
        <Row gutter={[24, 24]} justify={'center'} align='top'>
          <Col xs={24} lg={12}>
            <Flex flex={1} gap='large' style={{ marginTop: 15 }} vertical justify='center' align='center'>
              <Text>Платежі приймаються</Text>
              <MasterCardLogo width={100} height={100} />
              <VisaLogo width={100} height={100} />
            </Flex>
          </Col>
          <Col xs={24} lg={12}>
            <Flex gap='large' vertical>
              <Title level={3} style={{ marginTop: 0 }}>Зворотній зв'язок</Title>
              <Text>Telegram: <a href='https://t.me/onegetman'>Перший Гетьман</a></Text>
              <Text>Телефон: <a href='tel:+380500401898'>+380(50) 040 18 98</a></Text>
              <Text>Обслуговування, ремонт і поліпшення прибудинкової території.<br /><Link to='/about'>ТОВ КЕРУЮЧА КОМПАНІЯ ГЕТЬМАН-1</Link></Text>
              <Text><Link to='/offer_agreement'>Публічний договір</Link></Text>
              <Text>Юридична адреса: 03057, м.Київ, вул.Вадима Гетьмана, буд.1</Text>
            </Flex>
          </Col>
        </Row>
      </Footer>
    </Layout>

  );
};
export default LayoutComponent;
